import React, { useEffect, useRef } from 'react';

import classNames from 'classnames';

import AmbaInput from '@/components/global/AmbaInput';
import Button from '@/components/global/Button';
import { Link } from '@/components/global/Link';
import { getUrlWithHost } from '@/helpers/url';
import { isValidEmail } from '@/helpers/validators';
import { useTranslation } from '@/i18n';
import { LoginFormType, LoginValidationResponse } from '@/models/authModels';
import { AmbaInputTypes } from '@/models/globalModels';
import { envVars } from '@/services/ApiService';

import styles from './index.module.scss';

export type LoginFormProps = {
  onSubmit: (form: LoginFormType) => void;
  isLoading?: boolean;
  validationResponse?: LoginValidationResponse;
};

const LoginForm = ({ onSubmit, isLoading = false, validationResponse }: LoginFormProps) => {
  const { t } = useTranslation('auth');
  const [loginForm, setLoginForm] = React.useState({ email: '', password: '' });
  const [clientErrors, setClientErrors] = React.useState<{ email?: string; password?: string }>({});

  // Refs for inputs  
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Syncs browser autofill with state
  useEffect(() => {
    setTimeout(() => {
      const email = emailRef.current?.value || '';
      const password = passwordRef.current?.value || '';
      setLoginForm({ email, password });
    }, 100);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const data = { id: e.target.id, value: e.target.value };
    setLoginForm({ ...loginForm, [data.id]: data.value });
    setClientErrors((prev) => ({ ...prev, [data.id]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const errors: { email?: string; password?: string } = {};
    if (!isValidEmail(loginForm.email)) errors.email = t('login.emailRequired');
    if (!loginForm.password) errors.password = t('login.passwordRequired');
    if (errors.email || errors.password) {
      setClientErrors(errors);
      return;
    }
    onSubmit({ ...loginForm });
  };

  const getValidationMessage = (key: string) => {
    const clientError = clientErrors[key as 'email' | 'password'];
    if (clientError) return clientError;
    if (isFieldInvalid(key) && validationResponse) {
      return validationResponse[key as keyof LoginValidationResponse][0];
    }
    return undefined;
  };

  const isFieldInvalid = (fieldName: string) => {
    if (!validationResponse || validationResponse == undefined) return false;
    return validationResponse[fieldName as keyof LoginValidationResponse] ? true : false;
  };

  return (
    <div className="LoginForm row mt-2">
      <div className="col-md-12">
        <form autoComplete="on" className="needs-validation" onSubmit={handleSubmit} noValidate>
          <AmbaInput
            ariaDescribedBy="emailHelp"
            autoComplete="username"
            extraClass="my-2 loginInput"
            extraIconClass="loginIcon"
            hasLiveValidation={true}
            hasValidation={true}
            icon="user"
            id="email"
            name="email"
            onChange={handleInputChange}
            placeHolder={t('login.email')}
            ref={emailRef}
            type={AmbaInputTypes.EMAIL}
            validationMessage={getValidationMessage('email')}
            validator={isValidEmail}
            value={loginForm.email}
          />
          <AmbaInput
            ariaDescribedBy="passwordHelp"
            autoComplete="current-password"
            extraClass={'my-2 loginInput'}
            extraIconClass="loginIcon"
            hasValidation={true}
            icon="lock"
            id="password"
            name="password"
            onChange={handleInputChange}
            placeHolder={t('login.password')}
            ref={passwordRef}
            type={AmbaInputTypes.PASSWORD}
            validationMessage={getValidationMessage('password')}
            value={loginForm.password}
          />
          <div className="d-flex align-items-center justify-content-center mt-4">
            <Button
              className={classNames(styles.loginBtn, 'w-75')}
              disabled={isLoading}
              loading={isLoading}
              text={t('login.login')}
              isSubmit
            />
          </div>
          <div className="m-auto text-center mt-2">
            <Link className={styles.forgotPasswordBtn} href={getUrlWithHost('/forgot', envVars.checkHost)}>
              {t('login.forgotPassword')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
